import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { blogService } from '../../../services/blogService';

const RecentBlogPosts = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const data = await blogService?.getPublishedBlogs();
      setBlogPosts(data?.slice(0, 3) || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAllPosts = () => {
    navigate('/blog-posts');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading blog posts...</p>
          </div>
        </div>
      </section>
    );
  }

  if (blogPosts?.length === 0) {
    return null;
  }

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <span className="inline-block px-4 py-2 bg-accent/10 text-accent rounded-full text-sm md:text-base font-medium mb-4">
            Blog
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Recent Articles
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            Insights and tutorials on web development and technology
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
          {blogPosts?.map((post) =>
          <article
            key={post?.id}
            className="group bg-card rounded-xl md:rounded-2xl overflow-hidden border border-border hover:border-accent/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2">

              <div className="relative h-48 md:h-56 overflow-hidden">
                <Image
                src={post?.image}
                alt={post?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-accent text-accent-foreground text-xs md:text-sm rounded-full font-medium">
                    {post?.category}
                  </span>
                </div>
              </div>

              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-3 text-xs md:text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    {formatDate(post?.publishedDate)}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    {post?.readTime} min read
                  </span>
                </div>

                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 line-clamp-2 group-hover:text-accent transition-colors duration-300">
                  {post?.title}
                </h3>

                <p className="text-sm md:text-base text-muted-foreground mb-4 line-clamp-3">
                  {post?.excerpt}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="text-sm text-muted-foreground">
                    By {post?.author?.fullName}
                  </span>
                  <Button
                  variant="ghost"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="group-hover:text-accent"
                  onClick={() => navigate(`/blog-posts/${post?.id}`)}>

                    Read More
                  </Button>
                </div>
              </div>
            </article>
          )}
        </div>

        <div className="text-center">
          <Button
            variant="default"
            size="lg"
            iconName="BookOpen"
            iconPosition="left"
            onClick={handleViewAllPosts}>

            View All Articles
          </Button>
        </div>
      </div>
    </section>);

};

export default RecentBlogPosts;